import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { getProvider, getRecentTransactions } from '../utils/ethersUtils';
import {
  Container, Box, Typography, Card, CardContent, Button, Table, TableBody, TableCell,
  TableHead, TableRow, CircularProgress, Fade, Chip, Stack
} from '@mui/material';
import { CheckCircle, Schedule, Error as ErrorIcon } from '@mui/icons-material';
import { ThemeProvider } from '@mui/material/styles';
import theme from '../utils/theme';

function TransactionHistory({ wallet }) {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const transactionsPerPage = 5;

  useEffect(() => {
    const fetchTransactions = async () => {
      if (!wallet?.address || !ethers.utils.isAddress(wallet.address)) {
        setError('No valid wallet address provided');
        return;
      }

      setLoading(true);
      setError('');

      try {
        const currentNetwork = 'homestead'; // Ajustar según la red actual
        const history = await getRecentTransactions(wallet.address, currentNetwork);
        setTransactions(history || []);
      } catch (err) {
        setError('Failed to load transaction history');
        console.error('Error fetching transactions:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, [wallet]);

  const indexOfLastTx = currentPage * transactionsPerPage;
  const indexOfFirstTx = indexOfLastTx - transactionsPerPage;
  const currentTxs = transactions.slice(indexOfFirstTx, indexOfLastTx);
  const totalPages = Math.ceil(transactions.length / transactionsPerPage);

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="sm" sx={{ py: 4, bgcolor: 'background.default', minHeight: '100vh' }}>
        <Typography variant="h5" sx={{ fontWeight: 500, color: 'text.primary', mb: 3 }}>
          Historial de Transacciones
        </Typography>

        {loading ? (
          <Fade in={loading}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 4 }}>
              <CircularProgress sx={{ color: 'primary.main' }} />
              <Typography variant="body1" sx={{ mt: 2, color: 'text.secondary' }}>
                Cargando transacciones...
              </Typography>
            </Box>
          </Fade>
        ) : error ? (
          <Typography variant="body1" color="error.main" sx={{ textAlign: 'center', py: 2 }}>
            {error}
          </Typography>
        ) : (
          <Fade in={!loading}>
            <Card elevation={3} sx={{ borderRadius: 3, bgcolor: 'background.paper' }}>
              <CardContent>
                {currentTxs.length > 0 ? (
                  <>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Estado</TableCell>
                          <TableCell>Valor</TableCell>
                          <TableCell>Destinatario</TableCell>
                          <TableCell>Fecha</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {currentTxs.map((tx, index) => (
                          <TableRow key={tx.hash || `tx-${index}`}>
                            <TableCell>
                              <Chip
                                icon={
                                  tx.status === 'success' ? <CheckCircle /> :
                                  tx.status === 'failed' ? <ErrorIcon /> : <Schedule />
                                }
                                label={tx.status}
                                color={
                                  tx.status === 'success' ? 'success' :
                                  tx.status === 'failed' ? 'error' : 'warning'
                                }
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              {parseFloat(tx.value || 0).toFixed(4)} ETH
                            </TableCell>
                            <TableCell>
                              {tx.to ? `${tx.to.substring(0, 6)}...${tx.to.substring(38)}` : 'Creación de contrato'}
                            </TableCell>
                            <TableCell>
                              {tx.timestamp && !isNaN(new Date(tx.timestamp).getTime())
                                ? new Date(tx.timestamp).toLocaleString('es-ES', {
                                    month: 'short',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })
                                : 'Fecha desconocida'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>

                    <Stack direction="row" justifyContent="center" spacing={2} sx={{ mt: 3 }}>
                      <Button
                        variant="outlined"
                        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                      >
                        Anterior
                      </Button>
                      <Typography variant="body2" sx={{ alignSelf: 'center' }}>
                        Página {currentPage} de {totalPages}
                      </Typography>
                      <Button
                        variant="outlined"
                        onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages || totalPages === 0}
                      >
                        Siguiente
                      </Button>
                    </Stack>
                  </>
                ) : (
                  <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    No se encontraron transacciones
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Fade>
        )}
      </Container>
    </ThemeProvider>
  );
}

export default TransactionHistory;